import { ActionType as AccessTokenActionType, Action as AccessTokenAction } from '~/AccessToken';
import { Pagination } from '~/Api/Pagination';
import { Product } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { Item } from '~/Api/User/ProductGroup/Product';

import GroupProductActionTypes from './ActionTypes';

export interface ItemsState {
	request: Promise<any>;
	isFetching: boolean;
	items: Item[];
	pagination: Pagination | null;
}

export enum ChangedItemStatusValue {
	changed = 'changed',
	updating = 'updating',
	saved = 'saved',
	failed = 'failed',
}

export type ChangedItemStatus =
	| ChangedItemStatusValue.changed
	| ChangedItemStatusValue.updating
	| ChangedItemStatusValue.saved
	| ChangedItemStatusValue.failed;

export interface State {
	byGroupId: { [key: string]: ItemsState };
	byGroupIdAndId: { [key: string]: Item };
	updatingGroupIds: Set<string>;
	changedItemsStatus: { [key: string]: { [key: string]: ChangedItemStatus } };
	removeRequest: Promise<any> | null;
	itemsPerPage: number;
}

function createState(): State {
	return {
		byGroupId: {},
		byGroupIdAndId: {},
		updatingGroupIds: new Set(),
		changedItemsStatus: {},
		removeRequest: null,
		itemsPerPage: 100,
	};
}

export type ReceiveAddToGroupAction = {
	type: GroupProductActionTypes.receiveAddToGroup;
	group: UserProductGroup;
	groupId?: string;
	products: Product[];
};

export type Action =
	| {
			type: GroupProductActionTypes.requestItems;
			request: Promise<any>;
			groupId: string;
	  }
	| {
			type: GroupProductActionTypes.receiveItems;
			request: Promise<any>;
			groupId: string;
			items: Item[];
			pagination: Pagination;
	  }
	| {
			type: GroupProductActionTypes.receiveItems;
			request: Promise<any>;
			groupId: string;
			items: null;
			pagination: null;
	  }
	| { type: GroupProductActionTypes.requestItem }
	| {
			type: GroupProductActionTypes.receiveItem;
			groupId: string;
			productId: string;
			item: Item | null;
	  }
	| { type: GroupProductActionTypes.requestAddToGroup; group: UserProductGroup }
	| ReceiveAddToGroupAction
	| { type: GroupProductActionTypes.requestAddToGroupBySearch; group: UserProductGroup }
	| { type: GroupProductActionTypes.receiveAddToGroupBySearch; group: UserProductGroup }
	| { type: GroupProductActionTypes.requestRemoveFromGroup; request: Promise<any> }
	| {
			type: GroupProductActionTypes.receiveRemoveFromGroup;
			request: Promise<any>;
			groupId: string;
			group?: UserProductGroup;
			products: Product[];
	  }
	| {
			type: GroupProductActionTypes.markPackageSizeForSingleUnitChanged;
			groupId: string;
			productId: string;
	  }
	| {
			type: GroupProductActionTypes.requestUpdatePackageSizeForSingleUnit;
			groupId: string;
			productId: string;
	  }
	| {
			type: GroupProductActionTypes.receiveUpdatePackageSizeForSingleUnit;
			groupId: string;
			productId: string;
			item: Item | null;
	  }
	| { type: GroupProductActionTypes.prune; groupId: string }
	| { type: GroupProductActionTypes.setupItemsPerPage; perPage: number };

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case GroupProductActionTypes.requestItems: {
			const productsState = state.byGroupId[action.groupId];

			return {
				...state,
				byGroupId: {
					...state.byGroupId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						items: productsState?.items || [],
						pagination: productsState?.pagination || null,
					},
				},
			};
		}

		case GroupProductActionTypes.receiveItems: {
			const productsState = state.byGroupId[action.groupId];
			if (!productsState || productsState.request !== action.request) {
				return state;
			}

			if (!action.items) {
				const newByGroupId = { ...state.byGroupId };
				delete newByGroupId[action.groupId];

				return {
					...state,
					byGroupId: newByGroupId,
				};
			}

			const { groupId, items, pagination } = action;

			const newItems = [...productsState.items];
			newItems.splice(pagination.getFirstIndex(), pagination.perPage, ...items);

			const byGroupIdAndId = { ...state.byGroupIdAndId };
			items.forEach((item) => (byGroupIdAndId[`${groupId}-${item.product.id}`] = item));

			return {
				...state,
				byGroupId: {
					...state.byGroupId,
					[groupId]: {
						...productsState,
						isFetching: false,
						items: newItems,
						pagination,
					},
				},
				byGroupIdAndId,
			};
		}

		case GroupProductActionTypes.requestItem:
			return state;

		case GroupProductActionTypes.receiveItem: {
			if (action.item === undefined) {
				return state;
			}

			const newState = {
				...state,
				byGroupIdAndId: {
					...state.byGroupIdAndId,
					...(action.item ? { [`${action.groupId}-${action.productId}`]: action.item } : {}),
				},
			};
			if (action.item) {
				updateItem(newState, action.groupId, action.item);
			}
			return newState;
		}

		case GroupProductActionTypes.requestAddToGroup:
		case GroupProductActionTypes.requestAddToGroupBySearch: {
			const updatingGroupIds = new Set(state.updatingGroupIds);
			updatingGroupIds.add(action.group.id);

			return {
				...state,
				updatingGroupIds,
			};
		}

		case GroupProductActionTypes.receiveAddToGroup:
		case GroupProductActionTypes.receiveAddToGroupBySearch: {
			const updatingGroupIds = new Set(state.updatingGroupIds);
			updatingGroupIds.delete(action.group.id);

			return {
				...state,
				updatingGroupIds,
			};
		}

		case GroupProductActionTypes.requestRemoveFromGroup:
			return { ...state, removeRequest: action.request };

		case GroupProductActionTypes.receiveRemoveFromGroup: {
			const productsState = state.byGroupId[action.groupId];
			const removeRequest = action.request === state.removeRequest ? null : state.removeRequest;
			if (!productsState) {
				return { ...state, removeRequest };
			}

			const removedIds = new Set(action.products.map((product) => product.id));

			return {
				...state,
				byGroupId: {
					...state.byGroupId,
					[action.groupId]: {
						...productsState,
						items: productsState.items.filter((item) => !removedIds.has(item.product.id)),
					},
				},
				removeRequest,
			};
		}

		case GroupProductActionTypes.markPackageSizeForSingleUnitChanged:
			return {
				...state,
				changedItemsStatus: {
					...state.changedItemsStatus,
					[action.groupId]: {
						...(state.changedItemsStatus[action.groupId] || {}),
						[action.productId]: ChangedItemStatusValue.changed,
					},
				},
			};

		case GroupProductActionTypes.requestUpdatePackageSizeForSingleUnit:
			return {
				...state,
				changedItemsStatus: {
					...state.changedItemsStatus,
					[action.groupId]: {
						...(state.changedItemsStatus[action.groupId] || {}),
						[action.productId]: ChangedItemStatusValue.updating,
					},
				},
			};

		case GroupProductActionTypes.receiveUpdatePackageSizeForSingleUnit: {
			const newState = {
				...state,
				changedItemsStatus: {
					...state.changedItemsStatus,
					[action.groupId]: {
						...(state.changedItemsStatus[action.groupId] || {}),
						[action.productId]: action.item ? ChangedItemStatusValue.saved : ChangedItemStatusValue.failed,
					},
				},
			};
			if (action.item) {
				updateItem(newState, action.groupId, action.item);
			}
			return newState;
		}

		case GroupProductActionTypes.prune: {
			const byGroupId = { ...state.byGroupId };
			const changedItemsStatus = { ...state.changedItemsStatus };

			delete byGroupId[action.groupId];
			delete changedItemsStatus[action.groupId];

			return {
				...state,
				byGroupId,
				changedItemsStatus,
			};
		}

		case GroupProductActionTypes.setupItemsPerPage:
			return { ...state, itemsPerPage: action.perPage };

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

function updateItem(state: State, groupId: string, item: Item) {
	const itemsState = state.byGroupId[groupId];
	if (!itemsState) {
		return;
	}

	state.byGroupId[groupId] = {
		...itemsState,
		items: itemsState.items.map((currentItem) => (currentItem.product.id === item.product.id ? item : currentItem)),
	};
}

export function selectItemsByGroupId(state: State, groupId: string): ItemsState {
	return state.byGroupId[groupId];
}

export function selectItemByGroupIdAndId(state: State, groupId: string, id: string): Item | null {
	return state.byGroupIdAndId[`${groupId}-${id}`];
}

export function selectChangedItemsStatus(state: State, groupId: string): { [key: string]: ChangedItemStatus } | null {
	return state.changedItemsStatus[groupId];
}
